import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingClashes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [loadingMotion, setLoadingMotion] = useState(false);

  // Step 1: user lists KEY VULNERABLE actors
  const [actorInput, setActorInput] = useState('');
  const [actors, setActors] = useState([]); // array of strings

  // Step 2: user defines clashes mapped to an actor and a brief argument
  const emptyClash = () => ({ clash: '', actor: '', side: 'Government', argument: '' });
  const [clashes, setClashes] = useState([emptyClash()]);

  // Feedback
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);

  // Example
  const [exampleLoading, setExampleLoading] = useState(false);
  const [aiExample, setAiExample] = useState(null);
  const [exampleActor, setExampleActor] = useState(''); // optional actor focus for example

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Motion
  const newMotion = async () => {
    setError('');
    setFeedback(null);
    setAiExample(null);
    setActors([]);
    setActorInput('');
    setClashes([emptyClash()]);
    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');
      setMotion(data.motion);
    } catch (e) {
      console.error(e);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  // Actors
  const addActor = () => {
    const v = actorInput.trim();
    if (!v) return;
    if (actors.includes(v)) {
      setActorInput('');
      return;
    }
    setActors((prev) => [...prev, v]);
    setActorInput('');
  };
  const removeActor = (name) => {
    setActors((prev) => prev.filter((a) => a !== name));
    // Clear any clash rows referencing this actor
    setClashes((prev) => prev.map((c) => (c.actor === name ? { ...c, actor: '' } : c)));
  };

  // Clashes
  const updateClash = (idx, field, value) => {
    setClashes((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const addClashRow = () => setClashes((prev) => [...prev, emptyClash()]);
  const removeClashRow = (idx) => setClashes((prev) => prev.filter((_, i) => i !== idx));

  // Submit for feedback (uses the FIRST valid clash row)
  const submit = async () => {
    setError('');
    setFeedback(null);

    if (!motion) {
      setError('Generate a motion first.');
      return;
    }

    // actors must be strings for the API
    const actorStrings = (actors || [])
      .map((a) => (typeof a === 'string' ? a : '').trim())
      .filter(Boolean);

    if (actorStrings.length === 0) {
      setError('Add at least one vulnerable actor.');
      return;
    }

    // find first valid clash row
    const firstValid = (clashes || []).find(
      (c) => c && c.actor && c.clash && c.side && c.argument
    );

    if (!firstValid) {
      setError('Add at least one clash with actor, clash description, side, and brief argument.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/clashes-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motion,
          vulnerable_actors: actorStrings,
          clash: {
            actor: firstValid.actor.trim(),
            clash_text: firstValid.clash.trim(),
            // Brief arguments: send to both sides consistently. If user chose Government in the row,
            // treat their brief text as the Gov line, otherwise Opp line.
            gov_argument: firstValid.side === 'Government' ? firstValid.argument.trim() : '',
            opp_argument: firstValid.side === 'Opposition' ? firstValid.argument.trim() : '',
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to evaluate clashes');
      setFeedback(data);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to evaluate clashes');
    } finally {
      setSubmitting(false);
    }
  };

  // AI example after feedback
  const getExample = async () => {
    if (!motion || !feedback) return;
    setExampleLoading(true);
    setAiExample(null);
    try {
      const res = await fetch(`${API_BASE}/api/clashes-example`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motion,
          actor: exampleActor || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate example');
      setAiExample(data);
    } catch (e) {
      console.error(e);
      setError('Failed to generate AI example.');
    } finally {
      setExampleLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Clashes Training</h1>
          <p className="text-sm text-gray-600">
            List key vulnerable actors first. Then define a clash, link it to an actor, and briefly state the likely argument for that actor from either side.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={newMotion} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Motion */}
      <Card title="Practice Setup" subtitle="Your motion for this drill">
        <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
        <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
          {motion || 'Click "New Motion" to begin.'}
        </div>
      </Card>

      {/* Actors */}
      <Card title="Key Vulnerable Actors" subtitle="Add the actors whose interests are most exposed by this motion">
        <div className="flex gap-2">
          <Input
            placeholder="e.g., Gig economy workers"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addActor();
              }
            }}
          />
        <Button variant="secondary" onClick={addActor}>+ Add</Button>
        </div>

        {actors.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {actors.map((a) => (
              <div key={a} className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm">
                <span className="text-gray-800">{a}</span>
                <button className="text-gray-500 hover:text-red-600" onClick={() => removeActor(a)}>×</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-gray-600">Tip: think target populations, regulators, firms, workers, minorities, future generations.</p>
        )}
      </Card>

      {/* Clashes input */}
      <Card title="Define Clashes" subtitle="Describe the clash, select the affected actor, choose a side, and give a brief argument">
        <div className="space-y-6">
          {clashes.map((c, idx) => (
            <div key={idx} className="rounded-lg border border-gray-200 p-4">
              <div className="grid gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Clash</label>
                  <Input
                    placeholder="e.g., Individual privacy vs. public security"
                    value={c.clash}
                    onChange={(e) => updateClash(idx, 'clash', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Actor</label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={c.actor}
                    onChange={(e) => updateClash(idx, 'actor', e.target.value)}
                  >
                    <option value="">Select an actor</option>
                    {actors.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Side</label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={c.side}
                    onChange={(e) => updateClash(idx, 'side', e.target.value)}
                  >
                    <option>Government</option>
                    <option>Opposition</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Brief Argument for This Actor</label>
                <Textarea
                  rows={2}
                  placeholder="One or two sentences that this side would make for this actor"
                  value={c.argument}
                  onChange={(e) => updateClash(idx, 'argument', e.target.value)}
                />
              </div>

              <div className="mt-3 flex justify-between">
                <span className="text-xs text-gray-500">
                  Tip: Keep it brief and clearly linked to the clash.
                </span>
                {clashes.length > 1 && (
                  <Button variant="ghost" className="text-red-600" onClick={() => removeClashRow(idx)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <Button variant="secondary" onClick={addClashRow}>+ Add Clash</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Evaluating…</span> : 'Submit for Feedback'}
            </Button>
          </div>

          {error && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </Card>

      {/* Feedback */}
      {feedback && (
        <>
          {/* Actors feedback */}
          {(Array.isArray(feedback.actors_feedback) && feedback.actors_feedback.length > 0) && (
            <Card title="Actors Feedback" subtitle="Relevance and notes on your selected actors">
              <div className="space-y-3">
                {feedback.actors_feedback.map((a, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <strong className="text-gray-900">{a.name || a.actor || `Actor ${i + 1}`}</strong>
                      {'relevance' in a && <span>Relevance: {Math.round(a.relevance)}/10</span>}
                    </div>
                    {a.comments && <p className="mt-1 text-gray-700 whitespace-pre-wrap">{a.comments}</p>}
                  </div>
                ))}
              </div>

              {Array.isArray(feedback.missed_key_actors) && feedback.missed_key_actors.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <strong>Potential Key Actors You Missed:</strong> {feedback.missed_key_actors.join(', ')}
                </div>
              )}
            </Card>
          )}

          {/* Single clash evaluation (matches server response) */}
          {feedback.clash_evaluation && (
            <Card title="Clash Evaluation" subtitle="Identification quality, actor fit, and argument relevance">
              <div className="text-sm text-gray-800 space-y-3">
                <div className="rounded-lg border border-gray-200 p-4 grid sm:grid-cols-4 gap-2">
                  {'scores' in feedback.clash_evaluation && typeof feedback.clash_evaluation.scores === 'object' && (
                    <>
                      {'clash_identification' in feedback.clash_evaluation.scores && (
                        <p><strong>Identification:</strong> {Math.round(feedback.clash_evaluation.scores.clash_identification)}/10</p>
                      )}
                      {'actor_selection' in feedback.clash_evaluation.scores && (
                        <p><strong>Actor Choice:</strong> {Math.round(feedback.clash_evaluation.scores.actor_selection)}/10</p>
                      )}
                      {'argument_relevance' in feedback.clash_evaluation.scores && (
                        <p><strong>Argument Relevance:</strong> {Math.round(feedback.clash_evaluation.scores.argument_relevance)}/10</p>
                      )}
                      {'overall' in feedback.clash_evaluation.scores && (
                        <p><strong>Overall:</strong> {Math.round(feedback.clash_evaluation.scores.overall)}/10</p>
                      )}
                    </>
                  )}
                </div>

                {feedback.clash_evaluation.comments && (
                  <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                    {feedback.clash_evaluation.comments}
                  </div>
                )}
                {feedback.clash_evaluation.suggestions && (
                  <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                    <strong>Suggestions:</strong> {feedback.clash_evaluation.suggestions}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* AI Example */}
          <Card title="AI Clash Example" subtitle="Optional: generate a compact example after seeing feedback">
            <div className="grid sm:grid-cols-5 gap-3">
              <div className="sm:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Focus Actor (optional)</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={exampleActor}
                  onChange={(e) => setExampleActor(e.target.value)}
                >
                  <option value="">No specific actor</option>
                  {actors.map((a) => <option key={a} value={a}>{a}</option>)}
                  {Array.isArray(feedback.missed_key_actors) && feedback.missed_key_actors.map((a) => (
                    <option key={`missed-${a}`} value={a}>{a} (missed)</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 flex items-end">
                <Button onClick={getExample} disabled={exampleLoading}>
                  {exampleLoading ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'Generate Example'}
                </Button>
              </div>
            </div>

            {aiExample && (
              <div className="mt-4 rounded-lg border border-gray-200 p-4 text-sm text-gray-800">
                {/* Our /api/clashes-example returns actor, clash_text, gov_argument, opp_argument */}
                <p><strong>Clash:</strong> {aiExample.clash_text}</p>
                <p><strong>Actor:</strong> {aiExample.actor}</p>
                <p><strong>Government:</strong> {aiExample.gov_argument}</p>
                <p><strong>Opposition:</strong> {aiExample.opp_argument}</p>
                {aiExample.notes && (
                  <p className="mt-2 text-gray-700 whitespace-pre-wrap">{aiExample.notes}</p>
                )}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
